'use strict';

/**
 * Media Overlay video controls.
 *
 * Handles the custom play/pause button and native progress indicator for
 * background videos rendered by media-overlay-video.isml.
 */

const SHORT_VIDEO_CLASS = 'mediaOverlay__videoWrapper--short';
const SHORT_VIDEO_MAX_DURATION = 5;

/**
 * @typedef {Object} MediaOverlayVideoControlsApi
 * @property {function(): void} init - Initializes media overlay video controls.
 * @property {function(HTMLVideoElement, boolean): void} setPlayingState - Syncs the play/pause button state for carousel-owned playback.
 */

/**
 * Formats seconds as M:SS or H:MM:SS.
 * @param {number} seconds - Video time in seconds.
 * @returns {string} Formatted time.
 * @private
 */
function _formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '0:00';

    const totalSeconds = Math.floor(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;
    const paddedSeconds =
        remainingSeconds < 10
            ? `0${remainingSeconds}`
            : String(remainingSeconds);

    if (hours > 0) {
        const paddedMinutes = minutes < 10 ? `0${minutes}` : String(minutes);

        return `${hours}:${paddedMinutes}:${paddedSeconds}`;
    }

    return `${minutes}:${paddedSeconds}`;
}

/**
 * Reads the current button label from its visually hidden text.
 * @param {HTMLButtonElement} button - Play/pause button.
 * @param {boolean} isPlaying - Whether the video is currently playing.
 * @returns {string} Button label.
 * @private
 */
function _getToggleLabel(button, isPlaying) {
    const selector = isPlaying
        ? '.mediaOverlay__videoToggleText--pause'
        : '.mediaOverlay__videoToggleText--play';
    const label = button.querySelector(selector);

    return label && label.textContent
        ? label.textContent.trim()
        : isPlaying
          ? 'Pause video'
          : 'Play video';
}

/**
 * Sets the visible and accessible play/pause button state.
 * @param {HTMLButtonElement} button - Play/pause button.
 * @param {boolean} isPlaying - Whether the video is currently playing.
 * @private
 */
function _updateToggle(button, isPlaying) {
    const label = _getToggleLabel(button, isPlaying);
    const playIcon = button.querySelector(
        '.mediaOverlay__videoToggleIcon--play',
    );
    const pauseIcon = button.querySelector(
        '.mediaOverlay__videoToggleIcon--pause',
    );
    const playText = button.querySelector(
        '.mediaOverlay__videoToggleText--play',
    );
    const pauseText = button.querySelector(
        '.mediaOverlay__videoToggleText--pause',
    );

    if (playIcon) playIcon.style.display = isPlaying ? 'none' : '';
    if (pauseIcon) pauseIcon.style.display = isPlaying ? '' : 'none';
    if (playText) playText.setAttribute('aria-hidden', String(isPlaying));
    if (pauseText) {
        pauseText.setAttribute('aria-hidden', String(!isPlaying));
    }

    button.classList.toggle('mediaOverlay__videoToggle--playing', isPlaying);
    button.setAttribute('title', label);
}

/**
 * Marks videos up to 5 seconds long with the short-video wrapper class so
 * variant-specific CSS can reveal controls on hover/focus intent.
 * @param {HTMLElement} root - Media overlay video wrapper.
 * @param {HTMLVideoElement} video - Video element.
 * @private
 */
function _syncShortVideoClass(root, video) {
    const duration = isFinite(video.duration) ? video.duration : 0;

    root.classList.toggle(
        SHORT_VIDEO_CLASS,
        duration > 0 && duration <= SHORT_VIDEO_MAX_DURATION,
    );
}

/**
 * Updates native progress values and accessible progress text.
 * @param {HTMLVideoElement} video - Video element.
 * @param {HTMLProgressElement} progress - Progress element.
 * @param {string} label - Base progress label.
 * @param {{duration: number, value: number, text: string}} state - Last rendered progress state.
 * @param {boolean} updateDuration - Whether to sync the max value.
 * @private
 */
function _updateProgress(video, progress, label, state, updateDuration) {
    const duration = isFinite(video.duration) ? video.duration : 0;
    const currentTime = isFinite(video.currentTime) ? video.currentTime : 0;
    const progressWrapper = progress.closest(
        '.mediaOverlay__videoProgressWrapper',
    );
    const videoRoot = progress.closest('[data-media-overlay-video]');
    const roundedDuration = Math.max(Math.round(duration * 10) / 10, 1);
    const roundedCurrentTime = Math.min(
        Math.round(currentTime * 10) / 10,
        roundedDuration,
    );
    const progressPercent =
        duration > 0
            ? Math.min(Math.max((currentTime / duration) * 100, 0), 100)
            : 0;
    const progressText = `${label} ${_formatTime(currentTime)}/${_formatTime(
        duration,
    )}`;

    if (updateDuration && state.duration !== roundedDuration) {
        progress.max = roundedDuration;
        state.duration = roundedDuration;
    }

    if (state.value !== roundedCurrentTime) {
        progress.value = duration > 0 ? roundedCurrentTime : 0;
        state.value = roundedCurrentTime;
    }

    if (state.text !== progressText) {
        progress.setAttribute('title', progressText);
        progress.setAttribute('aria-label', progressText);

        if (progressWrapper) {
            progressWrapper.setAttribute('title', progressText);
        }

        state.text = progressText;
    }

    if (videoRoot) {
        videoRoot.style.setProperty(
            '--media-overlay-video-progress',
            `${progressPercent}%`,
        );
    }
}

/**
 * Binds controls for a single media overlay video root element.
 * @param {HTMLElement} root - Media overlay video wrapper.
 * @private
 */
function _initMediaOverlayVideoRoot(root) {
    if (root.hasAttribute('data-media-overlay-video-initialized')) {
        return;
    }

    const video = root.querySelector('.mediaOverlay__video');
    const button = root.querySelector('.mediaOverlay__videoToggle');
    const progress = root.querySelector('.mediaOverlay__videoProgress');

    if (!video || !button || !progress) return;

    root.setAttribute('data-media-overlay-video-initialized', 'true');

    const progressLabel =
        progress.getAttribute('aria-label') ||
        progress.getAttribute('title') ||
        'Video progress';
    const progressState = {
        duration: Number(progress.max) || 1,
        value: Number(progress.value) || 0,
        text: '',
    };

    _updateToggle(button, video.hasAttribute('autoplay'));
    _updateProgress(video, progress, progressLabel, progressState, true);
    _syncShortVideoClass(root, video);

    button.addEventListener('click', (e) => {
        if (video.paused || video.ended) {
            const playPromise = video.play();

            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {
                    _updateToggle(button, false);
                });
            }
        } else {
            video.pause();
        }

        if (e.detail > 0) {
            button.blur();
        }
    });

    video.addEventListener('play', () => {
        _updateToggle(button, true);
    });

    video.addEventListener('pause', () => {
        _updateToggle(button, false);
    });

    video.addEventListener('ended', () => {
        _updateToggle(button, false);
        _updateProgress(video, progress, progressLabel, progressState, false);
    });

    video.addEventListener('loadedmetadata', () => {
        _syncShortVideoClass(root, video);
        _updateProgress(video, progress, progressLabel, progressState, true);
    });

    video.addEventListener('durationchange', () => {
        _syncShortVideoClass(root, video);
        _updateProgress(video, progress, progressLabel, progressState, true);
    });

    video.addEventListener('timeupdate', () => {
        _updateProgress(video, progress, progressLabel, progressState, false);
    });
}

/**
 * Syncs a video toggle from carousel-owned playback changes.
 * This is exposed through `window.mediaOverlayVideoControls.setPlayingState`.
 * @param {HTMLVideoElement} video - Video element.
 * @param {boolean} isPlaying - Whether the video is currently playing.
 * @private
 */
function _setVideoPlayingState(video, isPlaying) {
    const root = video.closest('[data-media-overlay-video]');
    const button = root
        ? root.querySelector('.mediaOverlay__videoToggle')
        : null;

    if (button) {
        _updateToggle(button, isPlaying);
    }
}

/**
 * Initializes all media overlay videos on the page.
 *
 * @private
 */
function _initVideos() {
    const videos = document.querySelectorAll('[data-media-overlay-video]');

    videos.forEach(_initMediaOverlayVideoRoot);
}

/**
 * Initializes media overlay video controls and exposes the public re-init/state
 * sync API used by carousel variants.
 */
export function initMediaOverlayVideos() {
    if (window.mediaOverlayVideoControls) {
        window.mediaOverlayVideoControls.init();
        return;
    }

    /** @type {MediaOverlayVideoControlsApi} */
    window.mediaOverlayVideoControls = {
        init: _initVideos,
        setPlayingState: _setVideoPlayingState,
    };

    _initVideos();
}
